import { useState, useEffect } from "react";
import { authClient, docsClient, Document } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreVertical, Eye, Edit, Trash2, RefreshCw } from "lucide-react";
import ViewDocumentDialog from "./ViewDocumentDialog";
import EditDocumentDialog from "./EditDocumentDialog";
import RefreshDocumentDialog from "./RefreshDocumentDialog";

interface DocumentActionsMenuProps {
  document: Document;
  onDocumentUpdated: () => void;
}

export default function DocumentActionsMenu({
  document,
  onDocumentUpdated,
}: DocumentActionsMenuProps) {
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [refreshOpen, setRefreshOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const { toast } = useToast();

  const canRetry =
    document.processing_status === "failed" || document.processing_status === "pending";

  // Check cooldown for refresh (5 minutes = 300000ms)
  useEffect(() => {
    const checkCooldown = () => {
      const lastRefreshStr = localStorage.getItem(`refresh_cooldown_${document.id}`);
      if (lastRefreshStr) {
        const lastRefresh = parseInt(lastRefreshStr, 10);
        const elapsed = Date.now() - lastRefresh;
        const remaining = Math.max(0, 300000 - elapsed);
        setCooldownRemaining(remaining);
      } else {
        setCooldownRemaining(0);
      }
    };

    checkCooldown();
    const interval = setInterval(checkCooldown, 1000); // Update every second

    return () => clearInterval(interval);
  }, [document.id]);

  const canRefreshNow = cooldownRemaining === 0;

  const formatCooldown = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const handleRetry = async () => {
    setRetrying(true);

    try {
      // Call the process-document edge function
      const { data: sessionData } = await authClient.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        throw new Error("Not authenticated");
      }

      // Use DOCS database URL where process-document edge function is deployed
      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/process-document`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ document_id: document.id }),
        }
      );

      // Handle response - may be empty or invalid JSON on error
      const responseText = await response.text();
      let result;
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch {
        console.error("Failed to parse response:", responseText);
        throw new Error(
          response.ok ? "Invalid response from server" : `Server error: ${response.status}`
        );
      }

      if (!response.ok) {
        throw new Error(result.error || `Failed to process document (${response.status})`);
      }

      toast({
        title: "Success",
        description: result.size
          ? `Document processed successfully (${Math.round(result.size / 1024)} KB)`
          : "Document processing started",
      });

      onDocumentUpdated();
    } catch (error) {
      console.error("Error retrying document:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process document",
        variant: "destructive",
      });
    } finally {
      setRetrying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);

    try {
      const { error } = await docsClient.from("documents").delete().eq("id", document.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Document deleted successfully",
      });

      setDeleteOpen(false);
      onDocumentUpdated();
    } catch (error) {
      console.error("Error deleting document:", error);
      toast({
        title: "Error",
        description: "Failed to delete document",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setViewOpen(true)}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setEditOpen(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setRefreshOpen(true)} data-testid="menu-item-refresh">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh Documentation
            {!canRefreshNow && (
              <span className="ml-2 text-xs text-muted-foreground">(recently refreshed)</span>
            )}
          </DropdownMenuItem>
          {canRetry && (
            <DropdownMenuItem onClick={handleRetry} disabled={retrying}>
              <RefreshCw className={`mr-2 h-4 w-4 ${retrying ? "animate-spin" : ""}`} />
              {retrying ? "Processing..." : "Retry Processing"}
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setDeleteOpen(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ViewDocumentDialog document={document} open={viewOpen} onOpenChange={setViewOpen} />

      <EditDocumentDialog
        document={document}
        open={editOpen}
        onOpenChange={setEditOpen}
        onDocumentUpdated={onDocumentUpdated}
      />

      <RefreshDocumentDialog
        document={document}
        open={refreshOpen}
        onOpenChange={setRefreshOpen}
        onDocumentRefreshed={onDocumentUpdated}
      />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{document.title || document.url}"? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
